import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ============================================================================
// TIPOS
// ============================================================================

export interface PopoverProps {
  /** Elemento que activa el popover (trigger) */
  trigger: ReactNode;
  /** Contenido del popover */
  children: ReactNode;
  /** Alineación del popover respecto al trigger */
  align?: 'start' | 'center' | 'end';
  /** Lado donde aparece el popover */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Ancho del popover */
  width?: 'auto' | 'trigger' | number;
  /** Clase CSS adicional para el contenedor del popover */
  className?: string;
  /** Clase CSS adicional para el contenido */
  contentClassName?: string;
  /** Si el popover está controlado externamente */
  open?: boolean;
  /** Callback cuando cambia el estado abierto/cerrado */
  onOpenChange?: (open: boolean) => void;
  /** Deshabilitar el popover */
  disabled?: boolean;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook para detectar clics fuera de un elemento
 */
function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
  handler: () => void
) {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        handler();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, triggerRef, handler]);
}

/**
 * Hook para cerrar con Escape
 */
function useEscapeKey(handler: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handler();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handler, enabled]);
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function Popover({
  trigger,
  children,
  align = 'start',
  side = 'bottom',
  width = 'auto',
  className = '',
  contentClassName = '',
  open: controlledOpen,
  onOpenChange,
  disabled = false,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Determinar si es controlado o no
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setOpen = (value: boolean) => {
    if (!isControlled) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };

  // Estado para el lado efectivo (puede cambiar si no hay espacio)
  const [effectiveSide, setEffectiveSide] = useState(side);

  // Calcular posición del popover
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      const contentRect = contentRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const gap = 8; // Espacio entre trigger y popover
      const viewportHeight = window.innerHeight;

      // Estimar altura del contenido (usar la real si está disponible, sino estimar)
      const contentHeight = contentRect?.height || 400;

      // Determinar el lado efectivo basado en el espacio disponible
      let actualSide = side;
      if (side === 'bottom') {
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        // Si no hay suficiente espacio abajo pero sí arriba, cambiar a top
        if (spaceBelow < contentHeight && spaceAbove > spaceBelow) {
          actualSide = 'top';
        }
      } else if (side === 'top') {
        const spaceAbove = triggerRect.top;
        const spaceBelow = viewportHeight - triggerRect.bottom;
        // Si no hay suficiente espacio arriba pero sí abajo, cambiar a bottom
        if (spaceAbove < contentHeight && spaceBelow > spaceAbove) {
          actualSide = 'bottom';
        }
      }
      setEffectiveSide(actualSide);

      let top = 0;
      let left = 0;

      // Calcular posición vertical
      switch (actualSide) {
        case 'bottom':
          top = triggerRect.bottom + gap;
          break;
        case 'top':
          top = triggerRect.top - gap;
          break;
        case 'left':
          top = triggerRect.top;
          left = triggerRect.left - gap;
          break;
        case 'right':
          top = triggerRect.top;
          left = triggerRect.right + gap;
          break;
      }

      // Calcular posición horizontal (para side top/bottom)
      if (actualSide === 'top' || actualSide === 'bottom') {
        switch (align) {
          case 'start':
            left = triggerRect.left;
            break;
          case 'center':
            left = triggerRect.left + triggerRect.width / 2;
            break;
          case 'end':
            left = triggerRect.right;
            break;
        }
      }

      setPosition({ top, left });
    };

    // Ejecutar después de que el contenido se renderice
    requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, side, align]);

  // Hooks para cerrar
  useClickOutside(contentRef, triggerRef, () => setOpen(false));
  useEscapeKey(() => setOpen(false), isOpen);

  // Calcular ancho del contenido
  const getWidthStyle = () => {
    if (width === 'auto') return {};
    if (width === 'trigger' && triggerRef.current) {
      return { width: triggerRef.current.offsetWidth };
    }
    if (typeof width === 'number') {
      return { width };
    }
    return {};
  };

  // Calcular transformación según alineación (usa effectiveSide para posición dinámica)
  const getTransformStyle = () => {
    if (effectiveSide === 'top') {
      switch (align) {
        case 'start':
          return 'translateY(-100%)';
        case 'center':
          return 'translate(-50%, -100%)';
        case 'end':
          return 'translate(-100%, -100%)';
      }
    }
    if (effectiveSide === 'bottom') {
      switch (align) {
        case 'start':
          return 'translateY(0)';
        case 'center':
          return 'translateX(-50%)';
        case 'end':
          return 'translateX(-100%)';
      }
    }
    if (effectiveSide === 'left') {
      return 'translateX(-100%)';
    }
    return '';
  };

  const handleTriggerClick = () => {
    if (!disabled) {
      setOpen(!isOpen);
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        className={disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {trigger}
      </div>

      {/* Portal del contenido */}
      {isOpen &&
        createPortal(
          <div
            ref={contentRef}
            className={`
              fixed z-50
              bg-white rounded-lg shadow-xl
              border border-gray-200
              animate-in fade-in-0 zoom-in-95
              duration-150
              ${contentClassName}
            `}
            style={{
              top: position.top,
              left: position.left,
              transform: getTransformStyle(),
              ...getWidthStyle(),
            }}
            role="dialog"
            aria-modal="true"
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTES
// ============================================================================

interface PopoverHeaderProps {
  children: ReactNode;
  className?: string;
  onClose?: () => void;
}

export function PopoverHeader({ children, className = '', onClose }: PopoverHeaderProps) {
  return (
    <div
      className={`
        flex items-center justify-between
        px-4 py-3
        border-b border-gray-100
        ${className}
      `}
    >
      <div className="font-semibold text-gray-900 text-sm">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Cerrar"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

interface PopoverContentProps {
  children: ReactNode;
  className?: string;
}

export function PopoverContent({ children, className = '' }: PopoverContentProps) {
  return <div className={`p-4 ${className}`}>{children}</div>;
}

interface PopoverFooterProps {
  children: ReactNode;
  className?: string;
}

export function PopoverFooter({ children, className = '' }: PopoverFooterProps) {
  return (
    <div
      className={`
        px-4 py-3
        border-t border-gray-100
        bg-gray-50
        rounded-b-lg
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// ============================================================================
// EXPORTACIONES
// ============================================================================

export default Popover;
